import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  CheckCircle as GoodIcon,
  ErrorOutline as CriticalIcon,
  RemoveCircleOutline as NeutralIcon,
  ReportProblemOutlined as WarningIcon,
} from '@mui/icons-material';
import { INK, MUTED_MARK, STATUS } from './vizTokens';
import { formatRelativeDays } from './cohortAnalytics';

const STATUS_ICON = {
  good: GoodIcon,
  warning: WarningIcon,
  serious: WarningIcon,
  critical: CriticalIcon,
  unknown: NeutralIcon,
};

// Status always ships as icon + word + colour. None of the three carries the
// meaning alone — the four status hues are deliberately not separable enough
// under colour-vision deficiency to be trusted on their own.
export const StatusBadge = ({ status = 'unknown', label, dense = false }) => {
  const Icon = STATUS_ICON[status] || NeutralIcon;
  const color = STATUS[status] || INK.muted;
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
      <Icon sx={{ fontSize: dense ? 15 : 17, color, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: INK.secondary, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

// A number on its own is not a decision. Every tile carries the comparison
// that makes its value actionable — a target, a cohort reference, or the
// denominator it came from — so "87%" can never be read without knowing
// 87% of what, and whether that is good.
export const StatTile = ({
  label,
  value,
  denominator,
  comparison,
  status,
  statusLabel,
  caption,
}) => (
  <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, height: '100%' }}>
    <Typography
      variant="overline"
      sx={{ color: INK.muted, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.6 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.25 }}>
      <Typography variant="h4" fontWeight={800} sx={{ color: INK.primary, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {denominator && (
        <Typography variant="body2" sx={{ color: INK.muted, fontWeight: 600 }}>
          {denominator}
        </Typography>
      )}
    </Stack>
    {comparison && (
      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mt: 0.75 }}>
        {comparison.direction === 'up' && <UpIcon sx={{ fontSize: 15, color: STATUS.good }} />}
        {comparison.direction === 'down' && <DownIcon sx={{ fontSize: 15, color: STATUS.critical }} />}
        <Typography variant="caption" sx={{ color: INK.secondary, fontWeight: 700 }}>
          {comparison.label}
        </Typography>
      </Stack>
    )}
    {status && statusLabel && (
      <Box sx={{ mt: 0.75 }}>
        <StatusBadge status={status} label={statusLabel} dense />
      </Box>
    )}
    {caption && (
      <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 0.75, lineHeight: 1.5 }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

// The queue is the point of the dashboard. The signals endpoint already
// computes a status, the reasons behind it, and a recommended next action per
// learner — the previous UI fetched all of that and rendered none of it, so an
// admin could see that someone was struggling but not what to do about it.
export const ActionQueue = ({ items = [], emptyMessage = 'Nobody needs follow-up right now.' }) => {
  if (!items.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: INK.primary }}>
          Nothing needs intervention
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
          <GoodIcon sx={{ fontSize: 18, color: STATUS.good }} />
          <Typography variant="body2" sx={{ color: INK.secondary }}>{emptyMessage}</Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Typography
        variant="overline"
        sx={{ color: INK.muted, fontWeight: 700, letterSpacing: '0.06em', display: 'block', lineHeight: 1.6 }}
      >
        Do this next
      </Typography>
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: INK.primary, mb: 1.5 }}>
        {items.length} learner{items.length === 1 ? '' : 's'} need action, most urgent first
      </Typography>

      <Stack divider={<Divider flexItem />} spacing={0}>
        {items.map((item) => {
          const severity = item.status === 'at_risk'
            ? 'critical'
            : item.status === 'waiting_setup'
              ? 'warning'
              : item.calibration.key === 'overconfident'
                ? 'serious'
                : 'unknown';
          return (
            <Box key={item.id} sx={{ py: 1.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 4,
                      alignSelf: 'stretch',
                      minHeight: 34,
                      borderRadius: 999,
                      bgcolor: STATUS[severity] || MUTED_MARK,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={800} sx={{ color: INK.primary }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: INK.muted }}>
                      last active {formatRelativeDays(item.lastActivityAt)}
                      {item.completionRate != null && ` · ${item.completionRate}% completion`}
                      {item.calibrationGap != null && ` · ${item.calibrationGap > 0 ? '+' : ''}${item.calibrationGap}pt calibration gap`}
                    </Typography>
                  </Box>
                </Stack>
                <StatusBadge
                  status={severity}
                  label={item.status === 'at_risk' ? 'At risk' : item.status === 'waiting_setup' ? 'Needs setup' : item.calibration.label}
                  dense
                />
              </Stack>

              {item.recommendedAction && (
                <Typography variant="body2" sx={{ color: INK.secondary, mt: 0.75, pl: 2.1, lineHeight: 1.6 }}>
                  {item.recommendedAction}
                </Typography>
              )}

              {item.signals?.length > 0 && (
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, pl: 2.1 }}>
                  {item.signals.slice(0, 3).map((signal) => (
                    <Chip
                      key={signal}
                      size="small"
                      label={signal}
                      variant="outlined"
                      sx={{ fontSize: 11, height: 22, color: INK.secondary, borderColor: INK.grid }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

// Replaces the free-text "Data Story" blocks, which restated the method
// ("DCC is the theory signal...") rather than reporting what the current data
// says. This one is generated from the numbers, so it changes when they do.
export const ReadThis = ({ headline, points = [], status = 'unknown' }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
    <Typography
      variant="overline"
      sx={{ color: INK.muted, fontWeight: 700, letterSpacing: '0.06em', display: 'block', lineHeight: 1.6 }}
    >
      What this says
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.5, mb: 1.5 }}>
      {React.createElement(STATUS_ICON[status] || NeutralIcon, {
        sx: { fontSize: 20, color: STATUS[status] || INK.muted, flexShrink: 0, mt: '1px' },
      })}
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: INK.primary, lineHeight: 1.4 }}>
        {headline}
      </Typography>
    </Stack>
    <Stack component="ul" spacing={1} sx={{ pl: 2.5, m: 0 }}>
      {points.map((point, index) => (
        <Typography
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          component="li"
          variant="body2"
          sx={{ color: INK.secondary, lineHeight: 1.65 }}
        >
          {point}
        </Typography>
      ))}
    </Stack>
  </Paper>
);
